import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useConversations } from "../hooks/useConversations";
import {
  BookOpenIcon,
  BotIcon,
  Building2Icon,
  ChevronRightIcon,
  CreditCardIcon,
  FileTextIcon,
  FolderIcon,
  GraduationCapIcon,
  HistoryIcon,
  HomeIcon,
  LibraryIcon,
  LogOutIcon,
  MessageSquareIcon,
  MoonIcon,
  NetworkIcon,
  PencilIcon,
  PlusIcon,
  ScrollTextIcon,
  ServerIcon,
  SettingsIcon,
  ShieldIcon,
  SlidersHorizontalIcon,
  SunIcon,
  TerminalIcon,
  Trash2Icon,
  WrenchIcon,
} from "lucide-react";

import { useTheme } from "../context/ThemeContext";
import { getCachedProjects, listProjects } from "../api/projects";
import { useIsMobile } from "../hooks/useMediaQuery";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// Project avatar dot colors (DESIGN.md §2 "Categorical / data-viz palettes").
// These render straight into DOM inline styles, so plain `var(--chart-N)`
// strings resolve fine without going through src/lib/chartColors.ts (that
// helper is only needed where a *resolved* value is required, e.g. D3/canvas
// fill or alpha-composited strings — see LibraryPage.jsx's DOMAIN_COLORS).
const DOT_COLORS = ["var(--chart-2)", "var(--chart-3)", "var(--chart-6)", "var(--chart-4)", "var(--chart-7)"];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function initials(name, email) {
  if (name) return name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  if (email) return email[0].toUpperCase();
  return "U";
}

function trimTitle(raw = "") {
  let t = raw
    .replace(/^(hi|hello|hey)[,!]?\s+/i, "")
    .replace(/^(can you|could you|please)\s+/i, "")
    .trim();
  if (!t) t = raw.trim();
  return t.length > 28 ? t.slice(0, 27) + "…" : t;
}

function truncateProjectName(name = "", max = 22) {
  return name.length > max ? name.slice(0, max - 1) + "…" : name;
}

// ---------------------------------------------------------------------------
// Animation constants
// ---------------------------------------------------------------------------

// Keep in sync with --sidebar-w / --sidebar-collapsed-w in global.css
const SIDEBAR_WIDTH_PX = 260;
const SIDEBAR_COLLAPSED_WIDTH_PX = 68;

const SIDEBAR_VARIANTS = {
  open:   { width: SIDEBAR_WIDTH_PX },
  closed: { width: SIDEBAR_COLLAPSED_WIDTH_PX },
};

const LABEL_VARIANTS = {
  open:   { opacity: 1, x: 0,   transition: { duration: 0.15, ease: "easeOut" } },
  closed: { opacity: 0, x: -10, transition: { duration: 0.1,  ease: "easeIn"  } },
};

const SIDEBAR_TRANSITION = { type: "tween", ease: "easeOut", duration: 0.2 };

// ---------------------------------------------------------------------------
// NavGroup / ChildBtn helpers
// ---------------------------------------------------------------------------

function NavGroup({ icon, label, isOpen, onToggle, isActive, children, isCollapsed }) {
  return (
    <div className="sidebar-nav-group">
      <button
        type="button"
        className={`chat-history-btn sidebar-nav-group-btn${isActive ? " active" : ""}`}
        onClick={onToggle}
        title={isCollapsed ? label : undefined}
      >
        <span className="sidebar-nav-icon">{icon}</span>
        {!isCollapsed && (
          <motion.span
            className="sidebar-nav-group-label"
            variants={LABEL_VARIANTS}
            initial="closed"
            animate="open"
          >
            {label}
          </motion.span>
        )}
        {!isCollapsed && (
          <ChevronRightIcon
            size={13}
            className={`sidebar-nav-chevron${isOpen ? " open" : ""}`}
          />
        )}
      </button>
      {isOpen && !isCollapsed && <div className="sidebar-nav-children">{children}</div>}
    </div>
  );
}

function ChildBtn({ icon, label, onClick, isActive, disabled }) {
  return (
    <button
      type="button"
      className={`sidebar-nav-child-btn${isActive ? " active" : ""}${disabled ? " sidebar-nav-child-btn--disabled" : ""}`}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
    >
      {icon}
      {label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function Sidebar({
  userName,
  userEmail,
  conversations: conversationsProp = null,
  activeConversationId = null,
  onSelectConversation: onSelectConversationProp = null,
  onNewConversation = () => {},
  onNewDeveloperConversation: onNewDeveloperConversationProp = null,
  onRenameConversation: onRenameConversationProp = null,
  onDeleteConversation: onDeleteConversationProp = null,
  onLogout,
  onMobileClose = undefined, // injected by AppLayout via cloneElement
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, toggleTheme } = useTheme();
  const { user } = useAuth();
  // Pages may override via props (ChatPage/ChatsPage pass filtered lists and
  // custom handlers); everywhere else the sidebar is self-sufficient through
  // ConversationsContext, so Recent chats and the Tools actions work on every
  // page without prop threading.
  const conversationsCtx = useConversations();
  const conversations = conversationsProp ?? conversationsCtx.conversations;
  const onSelectConversation = onSelectConversationProp ?? conversationsCtx.selectConversation;
  const onNewDeveloperConversation = onNewDeveloperConversationProp ?? conversationsCtx.createDeveloperConversation;
  const onRenameConversation = onRenameConversationProp ?? conversationsCtx.renameConversation;
  const onDeleteConversation = onDeleteConversationProp ?? conversationsCtx.deleteConversation;
  const isOrgAdmin = user?.organization_membership?.role === "org_admin";
  const isPlatformAdmin = user?.role === "admin";
  const nav = (path) => { onMobileClose?.(); navigate(path); };

  // Hover-collapse only applies on desktop. On mobile (<=960px) the sidebar is
  // a slide-out drawer with no hover, so it must always render expanded.
  const [hoverCollapsed, setHoverCollapsed] = useState(true);
  const isMobile = useIsMobile();
  const isCollapsed = hoverCollapsed && !isMobile;

  const [editingId, setEditingId]     = useState(null);
  const [draftTitle, setDraftTitle]   = useState("");
  const [menuOpen, setMenuOpen]       = useState(false);
  const [logoutConfirm, setLogoutConfirm] = useState(false);
  const [openGroups, setOpenGroups]   = useState({
    tools: false,
    configurations: false,
    projects: false,
    library: false,
  });
  const [sidebarProjects, setSidebarProjects] = useState([]);
  const menuRef = useRef(null);

  const toggleGroup = (key) => setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));

  // ── Self-fetch projects from shared cache ────────────────────────────────
  useEffect(() => {
    if (!user) return;
    const cached = getCachedProjects(user);
    if (cached !== null) setSidebarProjects(cached);
    listProjects(user).then(setSidebarProjects).catch(() => {});
  }, [user]);

  // ── Active-state helpers ─────────────────────────────────────────────────
  const path = location.pathname;
  const at    = (p) => path === p;
  const under = (p) => path === p || path.startsWith(p + "/");

  const toolsActive   = under("/chat") || under("/developer") || at("/chats") || under("/agents");
  const configActive  = under("/settings") || under("/configurations");
  const libraryActive = under("/library");
  const projectsActive = under("/projects");

  // Auto-open the relevant group when navigating to a child route
  useEffect(() => {
    setOpenGroups((prev) => ({
      ...prev,
      tools:          prev.tools || toolsActive,
      configurations: prev.configurations || configActive,
      projects:       prev.projects || projectsActive,
      library:        prev.library || libraryActive,
    }));
  }, [path]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Conversation rename helpers ──────────────────────────────────────────
  const beginRename = (conversation) => {
    setEditingId(conversation.id);
    setDraftTitle(conversation.title);
  };

  const cancelRename = () => {
    setEditingId(null);
    setDraftTitle("");
  };

  const commitRename = async () => {
    if (!editingId) return;
    const title = draftTitle.trim();
    if (!title) { cancelRename(); return; }
    await onRenameConversation(editingId, title);
    cancelRename();
  };

  useEffect(() => {
    const onDocumentClick = (event) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(event.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDocumentClick);
    return () => document.removeEventListener("mousedown", onDocumentClick);
  }, []);

  // ── Shared navigation ────────────────────────────────────────────────────
  const sharedNav = (
    <>
      {/* Home */}
      <button
        type="button"
        className={`chat-history-btn${at("/home") ? " active" : ""}`}
        onClick={() => nav("/home")}
        title={isCollapsed ? "Home" : undefined}
      >
        <span className="sidebar-nav-icon"><HomeIcon size={14} /></span>
        {!isCollapsed && (
          <motion.span variants={LABEL_VARIANTS} initial="closed" animate="open">
            Home
          </motion.span>
        )}
      </button>

      {/* Tools */}
      <NavGroup
        icon={<WrenchIcon size={14} />}
        label="Tools"
        isOpen={openGroups.tools}
        onToggle={() => toggleGroup("tools")}
        isActive={toolsActive}
        isCollapsed={isCollapsed}
      >
        <ChildBtn icon={<BotIcon size={13} />} label="AI Agents" onClick={() => nav("/agents")} isActive={under("/agents")} />
        <ChildBtn icon={<TerminalIcon size={13} />}     label="AI Developer"   onClick={() => { onMobileClose?.(); onNewDeveloperConversation?.(); }}      isActive={under("/developer")} />
        <ChildBtn icon={<MessageSquareIcon size={13} />} label="AI Assistant"  onClick={() => nav("/chat")} />
        <ChildBtn icon={<HistoryIcon size={13} />}      label="Chats history"  onClick={() => nav("/chats")}                                              isActive={at("/chats")} />
      </NavGroup>

      {/* Configurations */}
      <NavGroup
        icon={<SlidersHorizontalIcon size={14} />}
        label="Configurations"
        isOpen={openGroups.configurations}
        onToggle={() => toggleGroup("configurations")}
        isActive={configActive}
        isCollapsed={isCollapsed}
      >
        <ChildBtn icon={<ServerIcon size={13} />} label="SAP General Settings" onClick={() => nav("/configurations/sap")} isActive={under("/configurations/sap")} />
        <ChildBtn icon={<ScrollTextIcon size={13} />} label="License Ruleset" onClick={() => nav("/configurations/license-ruleset")} isActive={under("/configurations/license-ruleset")} />
      </NavGroup>

      {/* Projects */}
      <NavGroup
        icon={<FolderIcon size={14} />}
        label="Projects"
        isOpen={openGroups.projects}
        onToggle={() => toggleGroup("projects")}
        isActive={projectsActive}
        isCollapsed={isCollapsed}
      >
        {sidebarProjects.slice(0, 5).map((project) => (
          <ChildBtn
            key={project.id}
            icon={<FolderIcon size={13} />}
            label={truncateProjectName(project.name)}
            onClick={() => nav(`/projects/${project.id}`)}
            isActive={at(`/projects/${project.id}`)}
          />
        ))}
        <ChildBtn icon={<PlusIcon size={13} />} label="View all projects" onClick={() => nav("/projects")} isActive={at("/projects")} />
      </NavGroup>

      {/* Library */}
      <NavGroup
        icon={<LibraryIcon size={14} />}
        label="Library"
        isOpen={openGroups.library}
        onToggle={() => toggleGroup("library")}
        isActive={libraryActive}
        isCollapsed={isCollapsed}
      >
        <ChildBtn icon={<FileTextIcon size={13} />} label="Documents"      onClick={() => nav("/library")}             isActive={libraryActive && !location.search.includes("tab=knowledge") && location.pathname !== "/library/cves"} />
        <ChildBtn icon={<NetworkIcon size={13} />}  label="Knowledge graph" onClick={() => nav("/library?tab=knowledge")} isActive={libraryActive && location.search.includes("tab=knowledge")} />
        <ChildBtn icon={<ShieldIcon size={13} />}   label="CVE Feed"       onClick={() => nav("/library/cves")}        isActive={location.pathname === "/library/cves"} />
      </NavGroup>

      {/* UAT Runner lives under Agents — no separate sidebar entry */}
      <button
        type="button"
        className={`chat-history-btn${under("/product-documentation") ? " active" : ""}`}
        onClick={() => nav("/product-documentation")}
        title={isCollapsed ? "Product Documentation" : undefined}
      >
        <span className="sidebar-nav-icon"><GraduationCapIcon size={14} /></span>
        {!isCollapsed && (
          <motion.span variants={LABEL_VARIANTS} initial="closed" animate="open">
            Product Documentation
          </motion.span>
        )}
      </button>

      <button
        type="button"
        className={`chat-history-btn${under("/blog") ? " active" : ""}`}
        onClick={() => nav("/blog")}
        title={isCollapsed ? "Blog" : undefined}
      >
        <span className="sidebar-nav-icon"><BookOpenIcon size={14} /></span>
        {!isCollapsed && (
          <motion.span variants={LABEL_VARIANTS} initial="closed" animate="open">
            Blog
          </motion.span>
        )}
      </button>

      {/* Admin / Org */}
      {isPlatformAdmin && (
        <button
          type="button"
          className={`chat-history-btn${at("/admin/organizations") ? " active" : ""}`}
          onClick={() => nav("/admin/organizations")}
          title={isCollapsed ? "Organizations" : undefined}
        >
          <span className="sidebar-nav-icon"><ShieldIcon size={14} /></span>
          {!isCollapsed && (
            <motion.span variants={LABEL_VARIANTS} initial="closed" animate="open">
              Organizations
            </motion.span>
          )}
        </button>
      )}
      {isOrgAdmin && (
        <button
          type="button"
          className={`chat-history-btn${at("/organization") ? " active" : ""}`}
          onClick={() => nav("/organization")}
          title={isCollapsed ? "My organization" : undefined}
        >
          <span className="sidebar-nav-icon"><Building2Icon size={14} /></span>
          {!isCollapsed && (
            <motion.span variants={LABEL_VARIANTS} initial="closed" animate="open">
              My organization
            </motion.span>
          )}
        </button>
      )}
    </>
  );

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <>
    <motion.aside
      className="sidebar"
      variants={SIDEBAR_VARIANTS}
      initial={isCollapsed ? "closed" : "open"}
      animate={isCollapsed ? "closed" : "open"}
      transition={SIDEBAR_TRANSITION}
      onMouseEnter={() => setHoverCollapsed(false)}
      onMouseLeave={() => setHoverCollapsed(true)}
    >
      <div className="sidebar-brand">
        {isCollapsed ? (
          <div className="sidebar-brand-icon" aria-label="tozenLabs">
            <span className="sidebar-brand-wordmark">
              <span className="sidebar-brand-tozen">t</span><span className="sidebar-brand-labs">L</span>
            </span>
          </div>
        ) : (
          <>
            <div className="sidebar-brand-wordmark" aria-label="tozenLabs">
              <span className="sidebar-brand-tozen">tozen</span>
              <span className="sidebar-brand-labs">Labs</span>
            </div>
            <span className="sidebar-beta-badge" aria-label="Beta version">BETA</span>
          </>
        )}
      </div>

      <div className="sidebar-scroll-area">
        <>
          {sharedNav}
          {!isCollapsed && conversations.length > 0 && <div className="sidebar-section-label">Recent</div>}
          {!isCollapsed && <div className="conversation-list">
            {conversations.slice(0, 2).map((conversation) => (
              <div
                key={conversation.id}
                className={conversation.id === activeConversationId ? "conversation-item active" : "conversation-item"}
              >
                {editingId === conversation.id ? (
                  <div className="conversation-inline-rename">
                    <input
                      className="conversation-rename-input"
                      value={draftTitle}
                      onChange={(e) => setDraftTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") commitRename();
                        if (e.key === "Escape") cancelRename();
                      }}
                      autoFocus
                    />
                    <button type="button" className="conversation-inline-action" onClick={commitRename}>Save</button>
                    <button type="button" className="conversation-inline-action danger" onClick={cancelRename}>Cancel</button>
                  </div>
                ) : (
                  <>
                    <button
                      type="button"
                      className="conversation-select-btn"
                      onClick={() => { onMobileClose?.(); onSelectConversation(conversation.id); }}
                      title={conversation.title}
                    >
                      {trimTitle(conversation.title)}
                    </button>
                    <div className="conv-actions">
                      <button
                        type="button"
                        className="conv-action-btn"
                        onClick={(e) => { e.stopPropagation(); beginRename(conversation); }}
                        title="Rename"
                      >
                        <PencilIcon size={12} />
                      </button>
                      <button
                        type="button"
                        className="conv-action-btn conv-action-btn--delete"
                        onClick={(e) => { e.stopPropagation(); onDeleteConversation(conversation.id, conversation); }}
                        title="Delete"
                      >
                        <Trash2Icon size={12} />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>}
        </>
      </div>{/* end sidebar-scroll-area */}

      <div className="sidebar-footer">
      {/* User row */}
      <div
        className={`sidebar-user-row${isCollapsed ? " sidebar-user-row--collapsed" : ""}`}
        ref={menuRef}
        onClick={() => !isCollapsed && setMenuOpen((p) => !p)}
        title={isCollapsed ? (userName || userEmail) : undefined}
      >
        <div className="sidebar-avatar">{initials(userName, userEmail)}</div>
        {!isCollapsed && (
          <>
            <div className="sidebar-user-info">
              <div className="sidebar-user">{userName || userEmail}</div>
              {userName && userEmail && <div className="sidebar-user-email">{userEmail}</div>}
            </div>
            <button
              type="button"
              className="settings-icon-btn"
              aria-label="Open account options"
              onClick={(e) => { e.stopPropagation(); setMenuOpen((p) => !p); }}
            >
              <SettingsIcon size={14} />
            </button>
          </>
        )}
        {menuOpen && !isCollapsed && (
          <div className="settings-popover" onClick={(e) => e.stopPropagation()}>
            <div className="settings-popover-theme">
              <span className="settings-popover-theme-label">
                {isDark ? <MoonIcon size={15} strokeWidth={2} /> : <SunIcon size={15} strokeWidth={2} />}
                Dark mode
              </span>
              <button
                type="button"
                className="theme-switch"
                role="switch"
                aria-checked={isDark}
                aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
                onClick={() => toggleTheme()}
              >
                <span className="theme-switch-thumb" aria-hidden />
              </button>
            </div>
            <button
              type="button"
              className="settings-popover-item"
              onClick={() => { setMenuOpen(false); nav("/settings"); }}
            >
              <SettingsIcon size={14} />
              User settings
            </button>
            <button
              type="button"
              className="settings-popover-item"
              onClick={() => { setMenuOpen(false); nav("/product-documentation"); }}
            >
              <GraduationCapIcon size={14} />
              Product Documentation
            </button>
            <button
              type="button"
              className="settings-popover-item"
              onClick={() => { setMenuOpen(false); nav("/settings?tab=license"); }}
            >
              <CreditCardIcon size={14} />
              License
            </button>
          </div>
        )}
      </div>

      <button
        type="button"
        className="logout-btn"
        onClick={() => setLogoutConfirm(true)}
        title={isCollapsed ? "Logout" : undefined}
      >
        <span className="sidebar-nav-icon"><LogOutIcon size={14} /></span>
        {!isCollapsed && (
          <motion.span variants={LABEL_VARIANTS} initial="closed" animate="open">
            Logout
          </motion.span>
        )}
      </button>
      </div>{/* end sidebar-footer */}
    </motion.aside>

    {logoutConfirm && (
      <div className="logout-modal-backdrop" onClick={() => setLogoutConfirm(false)}>
        <div className="logout-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
          <div className="logout-modal-icon"><LogOutIcon size={22} /></div>
          <h2 className="logout-modal-title">Log out?</h2>
          <p className="logout-modal-body">You'll need to sign in again to access your account.</p>
          <div className="logout-modal-actions">
            <button type="button" className="logout-modal-cancel" onClick={() => setLogoutConfirm(false)}>Cancel</button>
            <button type="button" className="logout-modal-confirm" onClick={onLogout}>Log out</button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
